import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { startCase } from 'lodash';
import { toast } from '@cotera/client/app/components';
import { useAuth } from '@workos-inc/authkit-react';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { Assert } from '@cotera/utilities';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useWorkOSDataProviders } from '@cotera/client/app/hooks/use-workos-data-providers';
import { OAuthConnectButton } from '@cotera/client/app/components/tools';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { PROVIDER_ICONS } from '../components/tool-connections/tool-connections-dialog';
import { InlineDeleteConfirmation } from '../components/tool-connections/credential-connection-cards';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

/**
 * OAuth providers managed elsewhere in settings (e.g. Chat Bots).
 *
 * The special case exclusion of microsoft-outseer-scoped is handled server-side in the
 * listWorkosDataProviders controller (tools-v2.controller.ts).
 */
const OAUTH_CONNECTIONS_EXCLUDED_SLUGS = new Set(['slack']);

const isExcludedOAuthProvider = (slug: string): boolean =>
  OAUTH_CONNECTIONS_EXCLUDED_SLUGS.has(slug.toLowerCase());

const OAuthProviderRow: React.FC<{
  provider: string;
  status: 'connected' | 'not_connected';
  onDisconnect: (provider: string) => Promise<void>;
  onConnectionChange: () => void;
}> = ({ provider, status, onDisconnect, onConnectionChange }) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const displayName = startCase(provider);
  const ProviderIcon = PROVIDER_ICONS[provider as keyof typeof PROVIDER_ICONS];
  const isConnected = status === 'connected';

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect(provider);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.toolsV2.workosDataProviders({ orgId }),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.toolsV2.allProviders({ orgId }),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.toolsV2.all({ orgId }),
      });
      toast.success(`Disconnected from ${displayName}`);
      setShowConfirm(false);
      onConnectionChange();
    } catch {
      toast.error(`Failed to disconnect from ${displayName}`);
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center">
              {ProviderIcon !== undefined ? (
                <ProviderIcon />
              ) : (
                <Icon
                  icon="puzzle-piece"
                  className="h-4 w-4 text-muted-foreground"
                />
              )}
            </div>
            <span className="font-medium">{displayName}</span>
          </div>
        </TableCell>
        <TableCell>
          <span
            className={`inline-flex items-center gap-1.5 text-xs ${
              isConnected ? 'text-green-600' : 'text-muted-foreground'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-muted-foreground'
              }`}
            />
            {isConnected ? 'Connected' : 'Not connected'}
          </span>
        </TableCell>
        <TableCell className="text-right">
          {isConnected ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConfirm((v) => !v)}
            >
              Disconnect
            </Button>
          ) : (
            <OAuthConnectButton
              displayName={displayName}
              provider={provider}
              onConnected={async () => {
                const syncResult =
                  await client.toolsV2.syncWorkosProviderAccount({
                    provider,
                  });
                Assert.assertOk(syncResult);
                onConnectionChange();
              }}
            />
          )}
        </TableCell>
      </TableRow>
      {showConfirm && (
        <TableRow>
          <TableCell colSpan={3} className="pt-0">
            <InlineDeleteConfirmation
              isPending={isDisconnecting}
              onConfirm={handleDisconnect}
              onCancel={() => setShowConfirm(false)}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export const OauthConnectionsSection: React.FC = () => {
  const { user } = useAuth();
  const { providers, disconnect, refetchProviders } = useWorkOSDataProviders();

  const visibleProviders = [...providers]
    .filter((p) => !isExcludedOAuthProvider(p.provider))
    .sort((a, b) => a.provider.localeCompare(b.provider));

  if (user === null) {
    return (
      <p className="text-sm text-muted-foreground">
        Sign in to manage OAuth connections.
      </p>
    );
  }

  if (visibleProviders.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No OAuth connections configured.
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs font-medium uppercase tracking-wide">
              Provider
            </TableHead>
            <TableHead className="text-xs font-medium uppercase tracking-wide">
              Status
            </TableHead>
            <TableHead className="w-[160px] text-xs font-medium uppercase tracking-wide text-right">
              Action
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleProviders.map((provider) => (
            <OAuthProviderRow
              key={provider.provider}
              provider={provider.provider}
              status={provider.status}
              onDisconnect={disconnect}
              onConnectionChange={() => {
                void refetchProviders();
              }}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
